import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {driver, Driver} from 'driver.js';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../../../store/auth';
import {MRAppRoutes} from '../../router/routes';
import {guidingTourLoadDetail} from '../../../store/guidingTour/actions';
import {useGuidingTour} from '../../../store/guidingTour/hooks';
import {useDispatch} from 'react-redux';
import './tour.scss';

type TourStep = {
  id: string;
  selector: string;
  route?: string;
  title: string;
  description: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  showButtons?: Array<'next' | 'previous' | 'close'>;
  nextButtonText?: string;
  previousButtonText?: string;
  beforeNext?: () => Promise<void> | void;
  beforePrev?: () => Promise<void> | void;
};

type ServerTourStepContent = {
  id?: string;
  selector?: string;
  route?: string;
  title?: string;
  description?: string;
  Description?: string;
  side?: string;
  align?: string;
  showButtons?: string | string[];
  NextButtonText?: string;
  nextBtnText?: string;
  PrevButtonText?: string;
  prevBtnText?: string;
};

const TOUR_START_EVENT = 'mr-guided-tour:start';
const TOUR_KEY = {type: 'GT', code: 'HOMEPAGE'};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForSelector = async (selector: string, timeoutMs = 7000) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const element = document.querySelector(selector) as HTMLElement | null;
    if (element && element.offsetParent !== null) {
      return element;
    }
    await wait(120);
  }
  return null;
};

const toPopoverSide = (value?: string): TourStep['side'] | undefined => {
  if (value === 'top' || value === 'bottom' || value === 'left' || value === 'right') {
    return value;
  }
  return undefined;
};

const toPopoverAlign = (value?: string): TourStep['align'] | undefined => {
  if (value === 'start' || value === 'center' || value === 'end') {
    return value;
  }
  return undefined;
};

const toAllowedButtons = (value?: string | string[]): TourStep['showButtons'] | undefined => {
  if (!value) return undefined;
  // Normalise: server may send a comma-separated string or a JSON array string
  const parts: string[] = Array.isArray(value)
    ? value
    : value
        .replace(/[\[\]'"]/g, '') // strip [ ] ' "
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  if (!parts.length) return undefined;
  const allowed = parts.filter(
    (button): button is 'next' | 'previous' | 'close' =>
      button === 'next' || button === 'previous' || button === 'close',
  );
  return allowed.length ? allowed : undefined;
};

const GuidingTourOrchestrator: React.FC = () => {
  const {auth} = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tour = useGuidingTour(TOUR_KEY.type, TOUR_KEY.code);

  const driverRef = useRef<Driver | null>(null);
  const activeRef = useRef(false);
  const stepIndexRef = useRef(0);

  const goToRoute = useCallback(
    async (route: string) => {
      const currentPath = window.location.hash.replace('#', '').split('?')[0];
      if (currentPath !== route) {
        navigate(route);
        await wait(350);
      }
    },
    [navigate],
  );

  const getFirstModelDetailRoute = useCallback(() => {
    const firstRow = document.querySelector('[data-row-key]') as HTMLElement | null;
    const uuid = firstRow?.getAttribute('data-row-key');
    return uuid ? `/model/${uuid}/detail` : MRAppRoutes.MODEL_LIST;
  }, []);

  const steps: TourStep[] = useMemo(() => {
    const defaultSteps: TourStep[] = [
      {
        id: 'welcome',
        selector: '[data-tour-id="filter-header"]',
        route: MRAppRoutes.FILTER_PAGE,
        title: 'Guided Tour of MR',
        description: 'We’ll walk you through key actions in Model Repository. Click Next to continue.',
        side: 'bottom',
        align: 'center',
      },
      {
        id: 'search',
        selector: '[data-tour-id="home-search-input"]',
        route: MRAppRoutes.FILTER_PAGE,
        title: 'Search',
        description: 'Enter keywords here to find models by name or metadata.',
        side: 'bottom',
        align: 'center',
      },
      {
        id: 'filters',
        selector: '[data-tour-id="home-filters"]',
        route: MRAppRoutes.FILTER_PAGE,
        title: 'Filters',
        description: 'Apply filters to narrow down your results and find models quickly.',
        side: 'top',
        align: 'center',
        beforeNext: async () => {
          await goToRoute(MRAppRoutes.MODEL_LIST);
        },
      },
      {
        id: 'model-entries-tab',
        selector: '[data-row-key]',
        route: MRAppRoutes.MODEL_LIST,
        title: 'Model Entries',
        description: 'This section lists all model entries you can browse and manage.',
        side: 'top',
        align: 'center',
      },
      {
        id: 'list-action-menu',
        selector: '[data-tour-id="model-row-action"]',
        route: MRAppRoutes.MODEL_LIST,
        title: 'Action Menu',
        description: 'Use this menu for quick actions like Edit Files, Edit Metadata, and Edit Permissions.',
        side: 'top',
        align: 'end',
        beforeNext: async () => {
          await goToRoute(getFirstModelDetailRoute());
        },
      },
      {
        id: 'detail-metadata',
        selector: '[data-tour-id="model-detail-card"]',
        title: 'Metadata Panel',
        description: 'View key metadata and core details of the selected model entry.',
        side: 'right',
        align: 'start',
      },
      {
        id: 'detail-overview-tab',
        selector: '[data-tour-id="model-detail-tabs"]',
        title: 'Overview',
        description: 'Overview shows model summary, AI context, and model family relationships.',
        side: 'bottom',
        align: 'center',
        beforeNext: async () => {
          const filesTab = document.querySelector('[data-tour-id="model-detail-tab-files"]') as HTMLElement | null;
          filesTab?.click();
          await wait(220);
        },
      },
      {
        id: 'detail-files-tab',
        selector: '[data-tour-id="model-detail-tabs"]',
        title: 'Model Files',
        description: 'Open Model Files to browse and download files for this entry.',
        side: 'bottom',
        align: 'center',
        beforePrev: async () => {
          const overviewTab = document.querySelector(
            '[data-tour-id="model-detail-tab-overview"]',
          ) as HTMLElement | null;
          overviewTab?.click();
          await wait(220);
        },
      },
      {
        id: 'detail-action-menu',
        selector: '[data-tour-id="model-detail-action-menu"]',
        title: 'Action Menu',
        description: 'Use this action menu on detail page for edit and lifecycle actions.',
        side: 'left',
        align: 'start',
        beforePrev: async () => {
          // Navigate back to files tab when going to previous step
          const filesTab = document.querySelector('[data-tour-id="model-detail-tab-files"]') as HTMLElement | null;
          filesTab?.click();
          await wait(220);
        },
      },
      {
        id: 'add-model-entry-tab',
        selector: '[data-tour-id="nav-add-model-entry"]',
        route: MRAppRoutes.MODEL_LIST,
        title: 'Add Model Entry',
        description: 'Create a new model entry in a guided step-by-step flow.',
        side: 'bottom',
        align: 'center',
        beforeNext: async () => {
          await goToRoute(MRAppRoutes.MODEL_LIST);
        },
        beforePrev: async () => {
          await goToRoute(getFirstModelDetailRoute());
        },
      },
    ];

    const contentById = new Map<string, ServerTourStepContent>();
    const parsedContentList: Array<{parsed: ServerTourStepContent; sourceIndex: number; apiOrderId?: number}> = [];

    (tour?.contentList || []).forEach((item, sourceIndex) => {
      if (!item?.content) {
        return;
      }

      try {
        const parsed = JSON.parse(item.content) as ServerTourStepContent;
        const rawApiId = (item as {id?: number | string}).id;
        const parsedApiId =
          typeof rawApiId === 'number' ? rawApiId : typeof rawApiId === 'string' ? Number(rawApiId) : NaN;
        const apiOrderId = Number.isNaN(parsedApiId) ? undefined : parsedApiId;

        parsedContentList.push({parsed, sourceIndex, apiOrderId});

        const contentId = (parsed.id || item.code || item.name || '').trim().toLowerCase();
        if (contentId) {
          contentById.set(contentId, parsed);
        }
      } catch {
        // Keep default step text when content is invalid JSON
      }
    });

    const contentByIndex = [...parsedContentList]
      .sort((a, b) => {
        const idA = a.apiOrderId;
        const idB = b.apiOrderId;

        if (idA == null && idB == null) return a.sourceIndex - b.sourceIndex;
        if (idA == null) return 1;
        if (idB == null) return -1;

        return idA - idB;
      })
      .map((item) => item.parsed);

    // For external users, filter to API-matched steps only
    // For internal users, use all default steps with API overrides
    const isExternalUser = auth.loggedInUser?.userType === 'EXTERNAL_USER';

    let stepsToUse = defaultSteps;
    if (isExternalUser && contentByIndex.length > 0) {
      // Filter steps based on API content availability for external users
      const filteredSteps: TourStep[] = [];
      let actionMenuCount = 0; // Track action menu occurrences to handle duplicates

      contentByIndex.forEach((content) => {
        const contentName = (content.title || '').toLowerCase().trim();
        let matchedStep: TourStep | undefined;

        // Find matching step by content name patterns
        if (contentName.includes('model entries')) {
          matchedStep = defaultSteps.find((s) => s.id === 'model-entries-tab');
        } else if (contentName.includes('metadata')) {
          matchedStep = defaultSteps.find((s) => s.id === 'detail-metadata');
        } else if (contentName.includes('overview')) {
          matchedStep = defaultSteps.find((s) => s.id === 'detail-overview-tab');
        } else if (contentName.includes('model files')) {
          matchedStep = defaultSteps.find((s) => s.id === 'detail-files-tab');
        } else if (contentName.includes('action menu')) {
          // Handle two "action menu" items: first is list-action-menu, second is detail-action-menu
          actionMenuCount++;
          if (actionMenuCount === 1) {
            matchedStep = defaultSteps.find((s) => s.id === 'list-action-menu');
          } else {
            matchedStep = defaultSteps.find((s) => s.id === 'detail-action-menu');
          }
        } else if (contentName.includes('add model')) {
          matchedStep = defaultSteps.find((s) => s.id === 'add-model-entry-tab');
        }

        if (matchedStep) {
          filteredSteps.push(matchedStep);
        }
      });

      stepsToUse = filteredSteps.length > 0 ? filteredSteps : defaultSteps;
    }

    return stepsToUse.map((step, index) => {
      const override = contentById.get(step.id.toLowerCase()) || contentByIndex[index];

      return {
        ...step,
        selector: override?.selector || step.selector,
        route: override?.route || step.route,
        title: override?.title || step.title,
        description: override?.description || override?.Description || step.description,
        side: toPopoverSide(override?.side) || step.side,
        align: toPopoverAlign(override?.align) || step.align,
        showButtons: toAllowedButtons(override?.showButtons),
        nextButtonText: override?.NextButtonText || override?.nextBtnText,
        previousButtonText: override?.PrevButtonText || override?.prevBtnText,
      };
    });
  }, [tour, getFirstModelDetailRoute, goToRoute, auth.loggedInUser]);

  const stopTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
    activeRef.current = false;
    stepIndexRef.current = 0;
  }, []);

  const showStep = useCallback(
    async (index: number) => {
      if (!activeRef.current) {
        return;
      }

      const step = steps[index];
      if (!step) {
        stopTour();
        return;
      }

      if (step.route) {
        await goToRoute(step.route);
      }

      // Use longer timeout for overview and files tabs which need time to render content
      const isDetailContentStep = step.id === 'detail-overview-tab' || step.id === 'detail-files-tab';
      const timeoutMs = isDetailContentStep ? 10000 : 7000;

      const element = await waitForSelector(step.selector, timeoutMs);
      if (!element) {
        stepIndexRef.current = index + 1;
        await showStep(index + 1);
        return;
      }

      // Match Figma behaviour: highlight list action menu button without opening the dropdown
      // (opening it programmatically causes it to render behind driver.js's overlay backdrop)
      if (step.id === 'list-action-menu') {
        // Close any already-open dropdown before highlighting
        try {
          const menu = document.querySelector(
            '[class*="ant-dropdown"][class*="ant-dropdown-placement"]',
          ) as HTMLElement | null;
          if (menu && menu.offsetParent !== null) {
            document.body.click();
            await wait(150);
          }
        } catch {
          // Continue even if check fails
        }
      }

      const totalSteps = steps.length;
      const isLast = index === totalSteps - 1;

      const defaultButtons: Array<'previous' | 'next' | 'close'> =
        index === 0 ? ['next', 'close'] : ['previous', 'next', 'close'];
      const mergedShowButtons: Array<'previous' | 'next' | 'close'> = step.showButtons
        ? step.showButtons.includes('close')
          ? [...step.showButtons]
          : ([...step.showButtons, 'close'] as Array<'previous' | 'next' | 'close'>)
        : defaultButtons;

      driverRef.current?.highlight({
        element,
        popover: {
          popoverClass: 'mr-guided-tour-popover',
          title: step.title,
          description: step.description,
          progressText: `${index + 1} / ${totalSteps}`,
          side: step.side || 'bottom',
          align: step.align || 'start',
          showButtons: mergedShowButtons,
          prevBtnText: step.previousButtonText || 'Previous Step',
          nextBtnText:
            step.nextButtonText || (isLast ? 'Finish the Tour' : index === 0 ? 'Start the Tour' : 'Next Step'),
          onNextClick: async () => {
            await step.beforeNext?.();
            stepIndexRef.current = index + 1;
            await showStep(index + 1);
          },
          onPrevClick: async () => {
            await step.beforePrev?.();
            const previousIndex = Math.max(index - 1, 0);
            stepIndexRef.current = previousIndex;
            await showStep(previousIndex);
          },
          onCloseClick: () => {
            stopTour();
          },
        },
      });
    },
    [goToRoute, steps, stopTour],
  );

  const startTour = useCallback(async () => {
    if (!auth.loggedInUser || activeRef.current) {
      return;
    }

    activeRef.current = true;

    // External users cannot access the filter page — start them from the Model List step
    const isExternalUser = auth.loggedInUser.userType === 'EXTERNAL_USER';
    let startIndex = 0;

    if (isExternalUser) {
      // For external users, find the first step that has a route to MODEL_LIST
      startIndex = steps.findIndex((s) => s.route === MRAppRoutes.MODEL_LIST);
      startIndex = startIndex >= 0 ? startIndex : 0;
    }

    stepIndexRef.current = startIndex;

    driverRef.current = driver({
      allowClose: true,
      disableActiveInteraction: true,
      stageRadius: 4,
      stagePadding: 6,
      showProgress: true,
      onDestroyed: () => {
        activeRef.current = false;
        stepIndexRef.current = 0;
        driverRef.current = null;
        guidingTourLoadDetail(dispatch, TOUR_KEY.type, TOUR_KEY.code);
      },
    });

    await showStep(0);
  }, [auth.loggedInUser, dispatch, showStep, steps]);

  const startTourRef = useRef(startTour);
  useEffect(() => {
    startTourRef.current = startTour;
  }, [startTour]);

  useEffect(() => {
    const handler = () => {
      startTourRef.current();
    };

    window.addEventListener(TOUR_START_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(TOUR_START_EVENT, handler as EventListener);
      stopTour();
    };
  }, [stopTour]);

  return null;
};

export default GuidingTourOrchestrator;
